import { z } from "zod";
import { COUNTRIES } from "./CampaignFormStep2";

// Schema for individual custom fields
export const customFieldSchema = z.object({
  name: z.string().nonempty("Field name is required.").optional(),
  type: z
    .enum(["string", "boolean", "number", "date"], {
      required_error: "Please select a field type.",
    })
    .optional(),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  negotiation: z.boolean().optional(),
  explanation: z.string().optional(),
  isCustom: z.boolean().optional(),
  default_value: z.number().optional(),
});

// Type for custom fields
export type CustomField = z.infer<typeof customFieldSchema>;

// --- Partnership Tier Schemas ---

export const PAID_COMPONENT_TYPES = ["max_budget", "max_cpm", "custom_formula"] as const;
// performance_cpm is affiliate-class (no fixed price to negotiate; pays from
// actual views after posting) — mirror of amy/lib/domain/tiers.ts.
export const AFFILIATE_COMPONENT_TYPES = ["cpa", "revenue_share", "other", "performance_cpm"] as const;
export const ALL_COMPONENT_TYPES = [...PAID_COMPONENT_TYPES, ...AFFILIATE_COMPONENT_TYPES] as const;
export const TIER_TYPES = ["paid", "affiliate", "combined"] as const;

export const customFormulaSchema = z.object({
  expression: z.string(),
  constants: z.array(z.object({ name: z.string(), value: z.number() })),
});

// Marginal CPM brackets settled after posting: each rate pays only the views
// inside its slice; floors are absolute view counts or multiples of the
// creator's median views. Cap is a required hard ceiling per creator.
export const performanceCpmSchema = z.object({
  boundary_mode: z.enum(["absolute", "median_multiple"]),
  rates: z
    .array(
      z.object({
        above: z.number({ invalid_type_error: "Bracket floor is required." }).min(0),
        cpm: z.number({ invalid_type_error: "CPM is required." }).positive("CPM must be positive."),
      })
    )
    .min(1, "Add at least one CPM bracket.")
    .max(3, "At most 3 CPM brackets."),
  cap: z
    .number({ invalid_type_error: "A max payout cap is required." })
    .positive("A max payout cap is required."),
  // absolute: cap is a fixed $ amount. median_cpm: cap is a CPM — each
  // creator's dollar cap = their median views × cap ÷ 1,000 (scales with size).
  cap_mode: z.enum(["absolute", "median_cpm"]),
  // Optional guaranteed minimum as a share of the cap (stored as a fraction,
  // entered as % in the UI): payout = clamp(ladder, flat_share × cap, cap).
  flat_share: z
    .number()
    .gt(0, "Must be above 0%.")
    .lt(1, "Must be below 100%.")
    .optional(),
  window_months: z
    .number({ invalid_type_error: "Measurement window is required." })
    .int()
    .min(1, "Minimum 1 month.")
    .max(24, "Maximum 24 months."),
});

export const componentSchema = z.object({
  type: z.enum(ALL_COMPONENT_TYPES),
  value: z.number().optional(),
  description: z.string().optional(),
  custom_formula: customFormulaSchema.optional(),
  performance_cpm: performanceCpmSchema.optional(),
});

export const tierSchema = z.object({
  order: z.number().int().positive(),
  tier_type: z.enum(TIER_TYPES),
  operator: z.enum(["and"]).nullable(),
  components: z.array(componentSchema).min(1).max(2),
});

export type TierComponent = z.infer<typeof componentSchema>;
export type Tier = z.infer<typeof tierSchema>;
export type CustomFormula = z.infer<typeof customFormulaSchema>;

// Complete form schema
export const formSchema = z
  .object({
    campaign_name: z.string().nonempty("Name is required.").max(60, {
      message: "Campaign name must be 60 characters or less.",
    }),
    follower_range_from: z.number({
      required_error: "Please select a minimum follower range.",
    }),
    follower_range_to: z.number({
      required_error: "Please select a maximum follower range.",
    }),
    number_of_creators: z
      .number()
      .min(10, { message: "Minimum 10 creators required" })
      .max(1000, { message: "Maximum 1000 creators allowed" }),
    number_of_deliverables: z
      .number({
        invalid_type_error: "Number of deliverables must be a number.",
      })
      .int("Number of deliverables must be a whole number.")
      .positive("Number of deliverables must be a positive number.")
      .optional(),
    countries: z
      .array(
        z.enum(
          COUNTRIES.map((country) => country.value) as [string, ...string[]]
        )
      )
      .min(1, {
        message: "Please select at least one country.",
      }),
    creator_prompt: z
      .string()
      .min(1, { message: "Please describe your ideal creator" }),
    /** Staff-only (uplodio_member): scope the creator search to one or more
     *  sourcing batches (raw_creators batch_name, comma-separated). Empty =
     *  search the whole corpus. */
    batch_name: z.string().optional(),
    platform: z.enum(["instagram", "tiktok", "youtube"], {
      required_error: "Please select a platform.",
    }),
    youtube_content_type: z.enum(["shorts", "long_form"]).optional(),

    // Campaign currency — every tier budget / negotiated price is denominated
    // in it. Immutable after creation.
    currency: z.enum(["USD", "EUR", "GBP"], {
      required_error: "Please select a currency.",
    }),

    // Partnership tiers (replaces flat partnership fields)
    tiers: z.array(tierSchema).min(1).max(4),

    fields: z
      .array(customFieldSchema)
      .refine((fields) => fields.some((field) => field.enabled === true), {
        message: "At least one custom field must be enabled",
        path: ["fields"],
      }),
    exclude_past_campaign_creators: z.boolean(),
    exclude_excluded_creators: z.boolean(),
    enable_smart_negotiation: z.boolean(),
    smart_negotiation_percentage: z
      .number({
        invalid_type_error: "Negotiation percentage must be a number.",
      })
      .int("Negotiation percentage must be a whole number.")
      .min(1, "Must be at least 1%")
      .max(99, "Must be at most 99%")
      .optional(),
    external_account_id: z.string({
      required_error: "Please select an external account.",
    }),
    use_creator_native_language: z.boolean(),
    use_dub: z.boolean().optional(),
    messaging: z.enum(["automatic", "approval"], {
      required_error: "Please select a messaging option.",
    }),
  })
  .refine(
    (data) => {
      const billingField = data.fields.find(
        (field) => field.name === "Billing-Info"
      );
      if (billingField?.enabled) {
        return billingField.explanation && billingField.explanation.length > 0;
      }
      return true;
    },
    {
      message: "Please explain how you pay creators when billing is enabled",
      path: ["fields"],
    }
  )
  .refine(
    (data) => {
      const fromValue = data.follower_range_from;
      const toValue = data.follower_range_to;
      return fromValue < toValue;
    },
    {
      message: "Minimum followers must be less than maximum followers",
      path: ["follower_range_to"],
    }
  )
  .superRefine((data, ctx) => {
    data.tiers.forEach((tier, tierIndex) => {
      // Combined tiers must have exactly 2 components
      if (tier.tier_type === "combined" && tier.components.length !== 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Combined tiers must have exactly 2 components",
          path: ["tiers", tierIndex, "components"],
        });
      }

      // Validate component types match tier_type
      const paidTypes = new Set(PAID_COMPONENT_TYPES);
      const affiliateTypes = new Set(AFFILIATE_COMPONENT_TYPES);

      tier.components.forEach((comp, compIndex) => {
        const basePath = ["tiers", tierIndex, "components", compIndex];

        // Type consistency check
        if (tier.tier_type === "paid" && !paidTypes.has(comp.type as any)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid component type for paid tier",
            path: [...basePath, "type"],
          });
        }
        if (tier.tier_type === "affiliate" && !affiliateTypes.has(comp.type as any)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid component type for affiliate tier",
            path: [...basePath, "type"],
          });
        }

        // Value validation per component type
        if (comp.type === "max_budget") {
          if (comp.value === undefined || comp.value <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Max budget per creator is required and must be positive.",
              path: [...basePath, "value"],
            });
          }
        }

        if (comp.type === "max_cpm") {
          if (comp.value === undefined || comp.value <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Max CPM per creator is required and must be positive.",
              path: [...basePath, "value"],
            });
          }
        }

        if (comp.type === "custom_formula") {
          if (
            !comp.custom_formula?.expression ||
            comp.custom_formula.expression.trim().length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Formula expression is required.",
              path: [...basePath, "custom_formula", "expression"],
            });
          }
        }

        if (comp.type === "cpa") {
          if (comp.value === undefined || comp.value <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "CPA value is required and must be positive.",
              path: [...basePath, "value"],
            });
          }
          if (!comp.description || comp.description.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "CPA description is required.",
              path: [...basePath, "description"],
            });
          }
        }

        if (comp.type === "revenue_share") {
          if (comp.value === undefined || comp.value <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Revenue share percentage is required and must be positive.",
              path: [...basePath, "value"],
            });
          }
        }

        if (comp.type === "other") {
          if (!comp.description || comp.description.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Please describe the compensation structure.",
              path: [...basePath, "description"],
            });
          }
        }

        if (comp.type === "performance_cpm") {
          const cfg = comp.performance_cpm;
          if (!cfg) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Configure the performance CPM brackets.",
              path: [...basePath, "performance_cpm"],
            });
          } else {
            for (let i = 1; i < cfg.rates.length; i++) {
              if (cfg.rates[i].above <= cfg.rates[i - 1].above) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: "Bracket floors must increase from one bracket to the next.",
                  path: [...basePath, "performance_cpm", "rates", i, "above"],
                });
              }
            }
          }
        }
      });
    });
  });

// Type for the complete form data
export type CampaignFormData = z.infer<typeof formSchema>;
